import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from 'app/common/logger';
import geoApiClient from 'app/api/GeoApi';
import { getHash } from 'app/redux/form/formSelectors';
import { getHasPartnerAccount } from 'app/redux/auth/authSelectors';
import * as LocalStorageService from '../../common/LocalStorageService';
import {
  getCountry,
  getIsCountryFinal,
} from '../../redux/localization/localizationSelectors';
import {
  getAuthTokenFromUrl,
  getCountryFromQuery,
  getHashFromCookies,
  getHashFromUrl,
} from '../../common/helpers';
import { setSettings } from '../../redux/settings/settingsActions';

const COUNTRIES_WITH_DISABLED_LIBRARIES = ['ge'];

const determineCurrentCountry = (
  countryFromQuery: string | null,
  countryFromState: string | undefined,
  isCountryFinal: boolean
): string | null => {
  if (countryFromQuery) {
    return countryFromQuery;
  }

  if (isCountryFinal && countryFromState) {
    return countryFromState;
  }

  return null;
};

const shouldLibrariesBeEnabled = (country: string): boolean => {
  return !COUNTRIES_WITH_DISABLED_LIBRARIES.includes(country.toLowerCase());
};

const hasAnyAuthProvided = (
  hashInState: string | null,
  hasPartnerAccountInState: boolean
): boolean => {
  const refreshTokenFromUrl = getAuthTokenFromUrl();
  const savedRefreshToken = LocalStorageService.getItem(
    LocalStorageService.refreshTokenKey
  );
  const queryHash = getHashFromUrl();
  const cookieHash = getHashFromCookies();
  return Boolean(
    refreshTokenFromUrl ||
      savedRefreshToken ||
      queryHash ||
      cookieHash ||
      hashInState ||
      hasPartnerAccountInState
  );
};

export const InitSettingsForLibraries: React.FC = () => {
  const isCountryFinal = useSelector(getIsCountryFinal);
  const countryFromState = useSelector(getCountry);
  const countryFromQuery = getCountryFromQuery();
  const hash = useSelector(getHash);
  const hasPartnerAccount = useSelector(getHasPartnerAccount);
  const dispatch = useDispatch();

  const hasAnyAuthData = hasAnyAuthProvided(hash, hasPartnerAccount);

  Logger.log('INIT_LIBRARIES state', {
    isCountryFinal,
    countryFromState,
    countryFromQuery,
    hash,
    hasPartnerAccount,
    hasAnyAuthData,
  });

  // Logic of this component:
  // - This component sets the value of "is_loading_libraries_enabled" based on the country.
  // - The difficult part is to determine the country, because in different scenarios it comes from
  //   different sources and at different moment of time.
  // - If we have country in URL it is enough to make decision.
  // - If we have any auth data (partner token or hash in query, cookies or local storage) we will wait for this data to load.
  // - When data is loaded (is_country_final set to true) - we can make decision.
  // - If we do not have auth data and country in URL - we determine country by IP.

  // Check if we have country in query or auth (partner token or hash)
  // and if not, look at the IP country and set the value based on it:
  useEffect(() => {
    const fetchIpLocation = async () => {
      if (countryFromQuery || hasAnyAuthData) {
        Logger.log('INIT_LIBRARIES skip IP location', {
          hasAnyAuthData,
          countryFromQuery,
        });
        return;
      }
      const geoApiResponse = await geoApiClient.getIpLocation();
      if (geoApiResponse?.data?.country_iso_code) {
        const ipCountry = geoApiResponse.data.country_iso_code;
        const librariesShouldBeEnabled = shouldLibrariesBeEnabled(ipCountry);
        dispatch(
          setSettings({
            is_loading_libraries_enabled: librariesShouldBeEnabled,
          })
        );
        Logger.log('INIT_LIBRARIES set based on IP', {
          ipCountry,
          librariesShouldBeEnabled,
        });
      }
    };
    fetchIpLocation();
  }, [hasAnyAuthData, countryFromQuery, dispatch]);

  // Set value in case we have value either from registration entity or from query
  useEffect(() => {
    const currentCountry = determineCurrentCountry(
      countryFromQuery,
      countryFromState,
      isCountryFinal
    );
    if (!currentCountry) {
      Logger.log('INIT_LIBRARIES no definite country', {
        countryFromQuery,
        countryFromState,
        isCountryFinal,
      });
      return;
    }
    const librariesShouldBeEnabled = shouldLibrariesBeEnabled(currentCountry);
    dispatch(
      setSettings({ is_loading_libraries_enabled: librariesShouldBeEnabled })
    );
    Logger.log('INIT_LIBRARIES set based on definite country', {
      currentCountry,
      librariesShouldBeEnabled,
    });
  }, [dispatch, isCountryFinal, countryFromQuery, countryFromState]);

  return null;
};
